import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { analyzeText, analyzeBarcode, analyzeImage, contributeProduct, checkBarcodeAvailability } from '@/lib/api';
import { isNative, takePhoto, pickPhoto, hapticFeedback } from '@/lib/capacitor';
import Quagga from '@ericblade/quagga2';
import { 
  Camera, 
  CameraOff, 
  Loader2, 
  Scan,
  Type,
  ArrowRight,
  AlertCircle,
  Keyboard,
  ShieldAlert,
  RefreshCw,
  Upload,
  Plus,
  Search,
  Package,
  CheckCircle2,
  XCircle,
  Image as ImageIcon
} from 'lucide-react';

const ScannerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scannerRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [lastScannedCode, setLastScannedCode] = useState(null);
  
  // Manual input states
  const [manualBarcode, setManualBarcode] = useState('');
  const [manualIngredients, setManualIngredients] = useState('');
  const [productName, setProductName] = useState('');
  
  // Not Found Dialog state
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);
  const [notFoundBarcode, setNotFoundBarcode] = useState('');
  const [notFoundMessage, setNotFoundMessage] = useState('');
  
  // Contribute Product Dialog state
  const [showContributeDialog, setShowContributeDialog] = useState(false);
  const [contributeData, setContributeData] = useState({
    barcode: '',
    product_name: '',
    brand: '',
    ingredients_raw: '',
    category: '',
    notes: ''
  });
  const [contributing, setContributing] = useState(false);
  
  // Image upload states
  const [imagePreview, setImagePreview] = useState(null);
  const [imageAnalyzing, setImageAnalyzing] = useState(false);
  const imageInputRef = useRef(null);

  // Check for barcode query param (from search page)
  useEffect(() => {
    const barcodeFromSearch = searchParams.get('barcode');
    if (barcodeFromSearch) {
      setManualBarcode(barcodeFromSearch);
      // Auto-switch to barcode tab and trigger search
      toast.info(`Barcode ${barcodeFromSearch} loaded. Click "Search Product" to analyze.`);
    }
  }, [searchParams]);

  // Check camera permission status on mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'camera' });
          setPermissionState(result.state);
          result.addEventListener('change', () => {
            setPermissionState(result.state);
          });
        }
      } catch (err) {
        // Some browsers don't support permission query for camera
        console.log('Permission query not supported');
      }
    };
    checkPermission();
  }, []);

  // Stop camera - MUST be defined before handleBarcodeDetected
  const stopCamera = useCallback(() => {
    try {
      Quagga.stop();
      Quagga.offDetected();
    } catch (e) {
      // Quagga might not be initialized yet - this is expected on first load
      console.debug('Quagga stop called before initialization:', e?.message);
    }
    setCameraActive(false);
  }, []);

  // Handle successful barcode detection
  const handleBarcodeDetected = useCallback(async (code) => {
    if (lastScannedCode === code) return; // Prevent duplicate scans
    setLastScannedCode(code);
    
    // Stop scanner and show analyzing state
    stopCamera();
    setAnalyzing(true);
    
    toast.success(`Barcode detected: ${code}`);
    
    try {
      const result = await analyzeBarcode(code, '');
      toast.success('Product analyzed successfully!');
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      
      if (error.response?.status === 404) {
        // Show the Not Found dialog with options
        setNotFoundBarcode(code);
        setNotFoundMessage(errorDetail);
        setShowNotFoundDialog(true);
        setManualBarcode(code);
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(errorDetail || 'Analysis failed. Please try again.');
      }
      setLastScannedCode(null);
    } finally {
      setAnalyzing(false);
    }
  }, [lastScannedCode, navigate, stopCamera]);

  // Start camera with Quagga barcode scanner
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setScanning(true);
      setLastScannedCode(null);

      // First, request camera permission explicitly
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' } 
        });
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());
        setPermissionState('granted');
      } catch (permErr) {
        console.error('Permission error:', permErr);
        if (permErr.name === 'NotAllowedError' || permErr.name === 'PermissionDeniedError') {
          setPermissionState('denied');
          setError('Camera permission denied. Please allow camera access in your browser settings and refresh the page.');
          setScanning(false);
          return;
        }
        throw permErr;
      }

      // Initialize Quagga barcode scanner
      await Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: scannerRef.current,
          constraints: {
            facingMode: "environment",
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
          },
        },
        locator: {
          patchSize: "medium",
          halfSample: true,
        },
        numOfWorkers: navigator.hardwareConcurrency || 4,
        decoder: {
          readers: [
            "ean_reader",
            "ean_8_reader",
            "upc_reader",
            "upc_e_reader",
            "code_128_reader",
            "code_39_reader",
          ],
        },
        locate: true,
      });

      Quagga.start();
      setCameraActive(true);
      setScanning(false);

      // Set up detection handler
      Quagga.onDetected((result) => {
        if (result && result.codeResult && result.codeResult.code) {
          const code = result.codeResult.code;
          // Play a beep sound for feedback
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 1000;
          gainNode.gain.value = 0.1;
          oscillator.start();
          setTimeout(() => oscillator.stop(), 100);
          
          // Haptic feedback for native apps
          hapticFeedback.success();
          
          handleBarcodeDetected(code);
        }
      });

    } catch (err) {
      console.error('Camera/Scanner error:', err);
      let errorMessage = 'Unable to start camera scanner. ';
      
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += 'Camera is in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage += 'Camera does not meet requirements.';
      } else {
        errorMessage += 'Please use manual barcode entry instead.';
      }
      
      setError(errorMessage);
      setCameraActive(false);
      setScanning(false);
    }
  }, [handleBarcodeDetected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        Quagga.stop();
        Quagga.offDetected();
      } catch (e) {
        // Quagga might not be initialized on unmount - this is expected
        console.debug('Quagga cleanup on unmount:', e?.message);
      }
    };
  }, []);

  // Handle barcode submission
  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!manualBarcode.trim()) return;
    
    setAnalyzing(true);
    try {
      const result = await analyzeBarcode(manualBarcode.trim(), productName);
      toast.success('Product analyzed successfully!');
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        const errorDetail = error.response?.data?.detail || '';
        setNotFoundBarcode(manualBarcode.trim());
        setNotFoundMessage(errorDetail);
        setShowNotFoundDialog(true);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
      }
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Handle opening contribute dialog
  const handleOpenContribute = () => {
    setContributeData({
      barcode: notFoundBarcode,
      product_name: productName || '',
      brand: '',
      ingredients_raw: '',
      category: '',
      notes: ''
    });
    setShowNotFoundDialog(false);
    setShowContributeDialog(true);
  };
  
  // Handle contribute product submission
  const handleContributeSubmit = async (e) => {
    e.preventDefault();
    if (!contributeData.barcode || !contributeData.product_name || !contributeData.ingredients_raw) {
      toast.error('Please fill in barcode, product name, and ingredients');
      return;
    }
    
    setContributing(true);
    try {
      await contributeProduct(contributeData);
      toast.success('Thank you! Product contributed successfully. Now analyzing...');
      setShowContributeDialog(false);
      
      // Now try to analyze the just-contributed product
      const result = await analyzeBarcode(contributeData.barcode, contributeData.product_name);
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to contribute product';
      toast.error(errorDetail);
    } finally {
      setContributing(false);
    }
  };
  
  // Handle switching to text/manual entry
  const handleSwitchToManualEntry = () => {
    setShowNotFoundDialog(false);
    setManualIngredients('');
    // Switch to the text tab
    const textTab = document.querySelector('[data-testid="tab-text"]');
    if (textTab) textTab.click();
  };

  // Handle text ingredients submission
  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!manualIngredients.trim()) return;
    
    setAnalyzing(true);
    try {
      const result = await analyzeText(manualIngredients, productName || 'Manual Entry');
      toast.success('Ingredients analyzed successfully!');
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Maximum size is 10MB.');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle image analysis
  const handleImageAnalyze = async () => {
    if (!imagePreview) {
      toast.error('Please select an image first');
      return;
    }
    
    setImageAnalyzing(true);
    try {
      // Extract base64 data from data URL
      const base64Data = imagePreview.split(',')[1] || imagePreview;
      
      const result = await analyzeImage(base64Data);
      
      // Haptic feedback on success (native only)
      await hapticFeedback.success();
      
      if (result.extracted_text) {
        toast.success('Image analyzed! Ingredients extracted.');
      } else {
        toast.success('Image analyzed successfully!');
      }
      
      navigate(`/analysis/${result.id}`);
    } catch (error) {
      await hapticFeedback.error();
      const errorMsg = error.response?.data?.detail || 'Failed to analyze image. Please try again or enter ingredients manually.';
      toast.error(errorMsg);
    } finally {
      setImageAnalyzing(false);
    }
  };

  // Handle native camera capture
  const handleNativeCamera = async () => {
    try {
      const photo = await takePhoto();
      if (photo && photo.dataUrl) {
        setImagePreview(photo.dataUrl);
        await hapticFeedback.light();
      }
    } catch (error) {
      toast.error('Failed to access camera');
    }
  };

  // Handle native gallery pick
  const handleNativeGallery = async () => {
    try {
      const photo = await pickPhoto();
      if (photo && photo.dataUrl) {
        setImagePreview(photo.dataUrl);
        await hapticFeedback.light();
      }
    } catch (error) {
      toast.error('Failed to access gallery');
    }
  };

  // Clear image preview
  const clearImagePreview = () => {
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="scanner-page">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Scan Product
          </h1>
          <p className="text-muted-foreground text-lg">
            Scan a barcode or enter ingredients manually
          </p>
          
          {/* Search Products Link */}
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/search')}
            data-testid="search-products-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            Search Products by Name
          </Button>
        </div>

        <Tabs defaultValue="camera" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="camera" data-testid="tab-camera">
              <Camera className="w-4 h-4 mr-2" />
              Camera
            </TabsTrigger>
            <TabsTrigger value="image" data-testid="tab-image">
              <ImageIcon className="w-4 h-4 mr-2" />
              Photo
            </TabsTrigger>
            <TabsTrigger value="barcode" data-testid="tab-barcode">
              <Keyboard className="w-4 h-4 mr-2" />
              Barcode
            </TabsTrigger>
            <TabsTrigger value="text" data-testid="tab-text">
              <Type className="w-4 h-4 mr-2" />
              Ingredients
            </TabsTrigger>
          </TabsList>

          {/* Camera Tab */}
          <TabsContent value="camera">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Camera Scanner</CardTitle>
                <CardDescription>
                  Point your camera at a product barcode to scan it automatically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Permission denied state */}
                {permissionState === 'denied' && (
                  <div className="flex flex-col items-center gap-4 p-6 bg-destructive/10 text-destructive rounded-lg">
                    <ShieldAlert className="w-12 h-12" />
                    <div className="text-center">
                      <p className="font-semibold mb-2">Camera Access Blocked</p>
                      <p className="text-sm mb-4">
                        To scan barcodes, you need to allow camera access. Please:
                      </p>
                      <ol className="text-sm text-left list-decimal list-inside space-y-1 mb-4">
                        <li>Click the camera/lock icon in your browser&apos;s address bar</li>
                        <li>Select &quot;Allow&quot; for camera access</li>
                        <li>Refresh this page</li>
                      </ol>
                      <Button 
                        variant="outline" 
                        onClick={() => window.location.reload()}
                        className="mt-2"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Page
                      </Button>
                    </div>
                  </div>
                )}

                {/* Other errors */}
                {error && permissionState !== 'denied' && (
                  <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}
                
                {/* Camera viewport */}
                <div 
                  ref={scannerRef}
                  className="camera-container mx-auto relative bg-muted rounded-xl overflow-hidden"
                  data-testid="scanner-viewport"
                >
                  {!cameraActive && !scanning && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
                      <div className="text-center p-6">
                        <Camera className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-2">Camera is off</p>
                        <p className="text-sm text-muted-foreground">
                          Click &quot;Start Scanner&quot; to begin scanning barcodes
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {scanning && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-20">
                      <div className="text-center text-white">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" />
                        <p className="font-medium">Starting camera...</p>
                        <p className="text-sm text-white/70 mt-1">Please allow camera access if prompted</p>
                      </div>
                    </div>
                  )}

                  {analyzing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-20">
                      <div className="text-center text-white">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" />
                        <p className="font-medium">Analyzing product...</p>
                        <p className="text-sm text-white/70 mt-1">Fetching ingredient information</p>
                      </div>
                    </div>
                  )}
                  
                  {cameraActive && !scanning && !analyzing && (
                    <div className="absolute inset-0 pointer-events-none z-10">
                      {/* Scanning overlay with corner brackets */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-36">
                        {/* Corner brackets */}
                        <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#5DBDB3]"></div>
                        <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#5DBDB3]"></div>
                        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#5DBDB3]"></div>
                        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#5DBDB3]"></div>
                        {/* Scanning line animation */}
                        <div className="absolute inset-x-2 top-2 bottom-2 overflow-hidden">
                          <div className="h-0.5 bg-[#F08B7D] animate-pulse" style={{
                            animation: 'scanLine 2s ease-in-out infinite'
                          }}></div>
                        </div>
                      </div>
                      {/* Instructions */}
                      <div className="absolute bottom-4 left-0 right-0 text-center">
                        <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-full">
                          Position barcode within the frame
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-center gap-3">
                  {!cameraActive ? (
                    <Button 
                      onClick={startCamera} 
                      data-testid="start-camera-btn"
                      disabled={scanning || permissionState === 'denied'}
                      className="bg-[#5DBDB3] hover:bg-[#4da8a0]"
                    >
                      {scanning ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          Start Scanner
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={stopCamera}
                      disabled={analyzing}
                      className="border-[#F08B7D] text-[#F08B7D] hover:bg-[#F08B7D]/10"
                    >
                      <CameraOff className="w-4 h-4 mr-2" />
                      Stop Scanner
                    </Button>
                  )}
                </div>

                {/* Help text */}
                <p className="text-center text-sm text-muted-foreground">
                  Supports EAN-13, EAN-8, UPC-A, UPC-E, Code 128, and Code 39 barcodes
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Image Upload Tab */}
          <TabsContent value="image">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-[#5DBDB3]" />
                  Photo of Ingredients
                </CardTitle>
                <CardDescription>
                  Take or upload a photo of the product&apos;s ingredient list. Our AI will extract and analyze the text.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upload area */}
                <div className="relative">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                    data-testid="image-upload-input"
                  />
                  
                  {!imagePreview ? (
                    <div className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-muted-foreground/30 rounded-lg">
                      <div className="text-center p-6">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#5DBDB3]/10 flex items-center justify-center">
                          <Upload className="w-8 h-8 text-[#5DBDB3]" />
                        </div>
                        <p className="text-lg font-medium mb-1">Capture ingredient photo</p>
                        <p className="text-sm text-muted-foreground mb-4">
                          {isNative() ? 'Use camera or select from gallery' : 'Take a photo or select from gallery'}
                        </p>
                        
                        {/* Native app buttons */}
                        {isNative() ? (
                          <div className="flex gap-3 justify-center">
                            <Button type="button" onClick={handleNativeCamera} data-testid="native-camera-btn">
                              <Camera className="w-4 h-4 mr-2" />
                              Camera
                            </Button>
                            <Button type="button" variant="outline" onClick={handleNativeGallery} data-testid="native-gallery-btn">
                              <ImageIcon className="w-4 h-4 mr-2" />
                              Gallery
                            </Button>
                          </div>
                        ) : (
                          <label htmlFor="image-upload" className="cursor-pointer">
                            <Button type="button" variant="outline" className="pointer-events-none">
                              <Camera className="w-4 h-4 mr-2" />
                              Select Image
                            </Button>
                          </label>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Ingredient list preview"
                        className="w-full h-64 object-contain rounded-lg border bg-muted"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={clearImagePreview}
                        data-testid="clear-image-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  )}
                </div>

                {/* Tips */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-[#5DBDB3]" />
                    Tips for best results
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Ensure the ingredient list is clearly visible and in focus</li>
                    <li>• Good lighting helps the AI read text more accurately</li>
                    <li>• Capture the entire ingredient list in one photo if possible</li>
                    <li>• Avoid shadows and glare on the packaging</li>
                  </ul>
                </div>

                {/* Analyze button */}
                <Button
                  onClick={handleImageAnalyze}
                  disabled={!imagePreview || imageAnalyzing}
                  className="w-full bg-[#5DBDB3] hover:bg-[#4AA89E]"
                  data-testid="analyze-image-btn"
                >
                  {imageAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing image...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" />
                      Analyze Ingredients
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Barcode Tab */}
          <TabsContent value="barcode">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Manual Barcode Entry</CardTitle>
                <CardDescription>
                  Enter the barcode number found on the product
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBarcodeSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="product-name">Product Name (optional)</Label>
                    <Input
                      id="product-name"
                      placeholder="e.g., Organic Granola Bar"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      data-testid="barcode-product-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="barcode">Barcode Number</Label>
                    <Input
                      id="barcode"
                      placeholder="e.g., 012345678901"
                      value={manualBarcode}
                      onChange={(e) => setManualBarcode(e.target.value)}
                      required
                      data-testid="barcode-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={analyzing || !manualBarcode.trim()}
                    data-testid="barcode-submit-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Looking up product...
                      </>
                    ) : (
                      <>
                        Search Product
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Text Tab */}
          <TabsContent value="text">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Paste Ingredients</CardTitle>
                <CardDescription>
                  Copy and paste the ingredient list from a product label
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTextSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="text-product-name">Product Name (optional)</Label>
                    <Input
                      id="text-product-name"
                      placeholder="e.g., Chocolate Chip Cookies"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      data-testid="text-product-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="ingredients">Ingredients</Label>
                    <Textarea
                      id="ingredients"
                      placeholder="Paste the full ingredient list here...&#10;&#10;Example: Water, Sugar, Natural Flavors, Citric Acid, Sodium Benzoate (Preservative), Yellow 5, Yellow 6"
                      value={manualIngredients}
                      onChange={(e) => setManualIngredients(e.target.value)}
                      className="min-h-[200px]"
                      required
                      data-testid="ingredients-textarea"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={analyzing || !manualIngredients.trim()}
                    data-testid="text-submit-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing ingredients...
                      </>
                    ) : (
                      <>
                        Analyze Ingredients
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Product Not Found Dialog */}
      <Dialog open={showNotFoundDialog} onOpenChange={setShowNotFoundDialog}>
        <DialogContent className="sm:max-w-md" data-testid="not-found-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-amber-500" />
              Product Not Found
            </DialogTitle>
            <DialogDescription className="text-left">
              {notFoundMessage || `Barcode ${notFoundBarcode} was not found in any of our databases.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              No worries! You have several options:
            </p>
            
            <div className="grid gap-3">
              {/* Option 1: Contribute Product */}
              <Button 
                onClick={handleOpenContribute}
                className="w-full justify-start h-auto py-3 px-4 bg-[#5DBDB3] hover:bg-[#4da8a0]"
                data-testid="contribute-product-btn"
              >
                <Plus className="w-5 h-5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">Contribute This Product</div>
                  <div className="text-xs opacity-80">Add it to our database for everyone</div>
                </div>
              </Button>
              
              {/* Option 2: Enter Ingredients Manually */}
              <Button 
                variant="outline"
                onClick={handleSwitchToManualEntry}
                className="w-full justify-start h-auto py-3 px-4"
                data-testid="manual-entry-btn"
              >
                <Type className="w-5 h-5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">Type Ingredients Manually</div>
                  <div className="text-xs text-muted-foreground">Copy from the product label</div>
                </div>
              </Button>
              
              {/* Option 3: Try Again */}
              <Button 
                variant="ghost"
                onClick={() => {
                  setShowNotFoundDialog(false);
                  setLastScannedCode(null);
                }}
                className="w-full justify-start h-auto py-3 px-4"
                data-testid="try-again-btn"
              >
                <RefreshCw className="w-5 h-5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-medium">Try a Different Barcode</div>
                  <div className="text-xs text-muted-foreground">Scan or enter another barcode</div>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Contribute Product Dialog */}
      <Dialog open={showContributeDialog} onOpenChange={setShowContributeDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contribute-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#5DBDB3]" />
              Contribute Product
            </DialogTitle>
            <DialogDescription>
              Help the community by adding this product to our database. Once submitted, it will be available for everyone!
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleContributeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contribute-barcode">Barcode *</Label>
                <Input
                  id="contribute-barcode"
                  value={contributeData.barcode}
                  onChange={(e) => setContributeData(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder="e.g., 5000157024671"
                  required
                  data-testid="contribute-barcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contribute-brand">Brand</Label>
                <Input
                  id="contribute-brand"
                  value={contributeData.brand}
                  onChange={(e) => setContributeData(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="e.g., Heinz, Tesco"
                  data-testid="contribute-brand-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contribute-name">Product Name *</Label>
              <Input
                id="contribute-name"
                value={contributeData.product_name}
                onChange={(e) => setContributeData(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder="e.g., Baked Beans in Tomato Sauce"
                required
                data-testid="contribute-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contribute-category">Category</Label>
              <Input
                id="contribute-category"
                value={contributeData.category}
                onChange={(e) => setContributeData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Canned Foods, Snacks, Beverages"
                data-testid="contribute-category-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contribute-ingredients">Ingredients List *</Label>
              <Textarea
                id="contribute-ingredients"
                value={contributeData.ingredients_raw}
                onChange={(e) => setContributeData(prev => ({ ...prev, ingredients_raw: e.target.value }))}
                placeholder="Copy the full ingredient list from the product label...&#10;&#10;Example: Beans (51%), Tomatoes (34%), Water, Sugar, Modified Cornflour, Salt, Spirit Vinegar, Onion Powder, Paprika Extract, Herb Extract"
                className="min-h-[150px]"
                required
                data-testid="contribute-ingredients-input"
              />
              <p className="text-xs text-muted-foreground">
                Please copy the ingredients exactly as they appear on the label
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contribute-notes">Additional Notes</Label>
              <Textarea
                id="contribute-notes"
                value={contributeData.notes}
                onChange={(e) => setContributeData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional information about this product..."
                className="min-h-[60px]"
                data-testid="contribute-notes-input"
              />
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowContributeDialog(false)}
                disabled={contributing}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={contributing || !contributeData.barcode || !contributeData.product_name || !contributeData.ingredients_raw}
                className="bg-[#5DBDB3] hover:bg-[#4da8a0]"
                data-testid="contribute-submit-btn"
              >
                {contributing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Contributing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Contribute & Analyze
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScannerPage;
